import React, { useState } from 'react';
import { Card, Radio, Select, DatePicker, Space } from 'antd';
import { CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;

export interface DateRange {
  start: string;
  end: string;
}

interface DateRangeSelectorProps {
  onRangeChange: (range: DateRange | null) => void;
  onModeChange: (mode: 'preset' | 'custom') => void;
  value?: DateRange | null;
  showPreset?: boolean;
}

const PRESET_KEYS = ['today', 'yesterday', 'week', 'month'] as const;
type PresetKey = (typeof PRESET_KEYS)[number];

const calculatePresetRange = (key: PresetKey): DateRange => {
  const now = dayjs();
  let start: dayjs.Dayjs;
  let end: dayjs.Dayjs;
  switch (key) {
    case 'today':
      start = now.startOf('day');
      end = now.endOf('day');
      break;
    case 'yesterday':
      start = now.subtract(1, 'day').startOf('day');
      end = now.subtract(1, 'day').endOf('day');
      break;
    case 'week':
      start = now.subtract(7, 'day').startOf('day');
      end = now.endOf('day');
      break;
    case 'month':
      start = now.subtract(30, 'day').startOf('day');
      end = now.endOf('day');
      break;
    default:
      start = now.startOf('day');
      end = now.endOf('day');
  }
  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
};

const rangesEqual = (a?: DateRange | null, b?: DateRange | null) => {
  if (!a || !b) return false;
  return dayjs(a.start).valueOf() === dayjs(b.start).valueOf() && dayjs(a.end).valueOf() === dayjs(b.end).valueOf();
};

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  onRangeChange,
  onModeChange,
  value,
  showPreset = true,
}) => {
  const [mode, setMode] = useState<'preset' | 'custom'>(showPreset ? 'preset' : 'custom');
  const [quickSelect, setQuickSelect] = useState<PresetKey>('today');
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);

  const handleModeChange = (newMode: 'preset' | 'custom') => {
    setMode(newMode);
    onModeChange(newMode);
    if (newMode === 'preset') {
      handleQuickSelect(quickSelect);
    }
  };



  const handleQuickSelectChange = (value: PresetKey) => {
    setQuickSelect(value);
    handleQuickSelect(value);
  };

  const handleQuickSelect = (value: PresetKey) => {
    const presetRange = calculatePresetRange(value);
    onRangeChange(presetRange);
  };

  const handleCustomRangeChange = (dates: [Dayjs, Dayjs] | null) => {
    if (dates && dates.length === 2 && dates[0] && dates[1]) {
      setCustomRange(dates);
      onRangeChange({
        start: dates[0].toISOString(),
        end: dates[1].toISOString()
      });
    } else {
      setCustomRange(null);
      onRangeChange(null);
    }
  };

  React.useEffect(() => {
    if (!value) {
      return;
    }
    if (!showPreset) {
      const start = dayjs(value.start);
      const end = dayjs(value.end);
      const hasChanged =
        !customRange ||
        !customRange[0].isSame(start) ||
        !customRange[1].isSame(end);
      if (mode !== 'custom') {
        setMode('custom');
      }
      if (hasChanged) {
        setCustomRange([start, end]);
      }
      return;
    }
    const matchingPreset = PRESET_KEYS.find((key) => rangesEqual(value, calculatePresetRange(key)));
    if (matchingPreset) {
      if (mode !== 'preset') {
        setMode('preset');
      }
      if (quickSelect !== matchingPreset) {
        setQuickSelect(matchingPreset);
      }
      if (customRange !== null) {
        setCustomRange(null);
      }
      return;
    }
    const start = dayjs(value.start);
    const end = dayjs(value.end);
    const hasChanged =
      !customRange ||
      !customRange[0].isSame(start) ||
      !customRange[1].isSame(end);
    if (mode !== 'custom') {
      setMode('custom');
    }
    if (hasChanged) {
      setCustomRange([start, end]);
    }
  }, [value, customRange, mode, quickSelect, showPreset]);

  return (
    <Card size="small" style={{ marginBottom: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        {showPreset && (
          <Radio.Group value={mode} onChange={(e) => handleModeChange(e.target.value)}>
            <Radio.Button value="preset">
              <ClockCircleOutlined /> プリセット期間
            </Radio.Button>
            <Radio.Button value="custom">
              <CalendarOutlined /> カスタム期間
            </Radio.Button>
          </Radio.Group>
        )}

        <Space wrap>
          {showPreset && mode === 'preset' ? (
            <Select<PresetKey>
              value={quickSelect}
              onChange={(value) => handleQuickSelectChange(value)}
              style={{ width: 120 }}
            >
              <Select.Option value="today">今日</Select.Option>
              <Select.Option value="yesterday">昨日</Select.Option>
              <Select.Option value="week">過去1週間</Select.Option>
              <Select.Option value="month">過去1ヶ月</Select.Option>
            </Select>
          ) : (
            <RangePicker
              showTime
              value={customRange}
              onChange={handleCustomRangeChange}
              placeholder={['開始時刻', '終了時刻']}
            />
          )}
        </Space>

      </Space>
    </Card>
  );
};

export default DateRangeSelector;
